import { useAuth0 } from '@auth0/auth0-react'
import { Alert } from '@designsforhealth/dfh-react-components'
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { TextField as MuiTextField } from '@material-ui/core'
import { ChevronRight } from '@material-ui/icons'
import { Subscribe } from '@react-rxjs/core'
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { Control, Controller, FormProvider, useForm } from 'react-hook-form'

import { SanityParentProductFragment } from '../../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import { ProductLabelStatus } from '../../../../graphql/search'
import { useCustomerId } from '../../../../hooks/useCustomerId'
import useIsTruemedAvailable from '../../../../hooks/useIsTruemedAvailable'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useUserCategory from '../../../../hooks/useUserCategory'
import { useUserAccessPolicy } from '../../../../lib/auth/user-access-policy'
import {
  autoShipIntervalOptions,
  resolveItemQuantityConfig,
  selectOptionsToDisplay,
  validAddQuantityOptionsFor,
} from '../../../../lib/cart/item-control-options'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { useDispensaryCartStatus } from '../../../../lib/dispensaries/events/cart'
import { useShowAutoShipAndSaveToUserGroup } from '../../../../lib/m2/user-groups'
import { prescriptionBuilderActions } from '../../../../lib/prescriptions/builder/actions'
import { Prescription } from '../../../../lib/prescriptions/builder/types'
import { useAddToPrescriptionToast } from '../../../../lib/prescriptions/hooks/useAddToPrescriptionToast'
import { usePrescriptionBuilder } from '../../../../lib/prescriptions/hooks/usePrescriptionBuilder'
import { usePayUpfrontOption } from '../../../../lib/products/hooks/usePayUpfrontOption'
import { useTestKitDelayedBilling } from '../../../../lib/products/hooks/useTestKitDelayedBilling'
import { useTestKitProducts } from '../../../../lib/products/hooks/useTestKitProducts'
import {
  AttributeName,
  AttributeValue,
  defaultItemFormValues,
  FormattedVariantData,
  isCombinationNotAvailable,
  maxItemOrderQuantityByUserCategory,
  stockMessageDisplay,
} from '../../../../utils/itemTools'
import { itemQuantityPrice } from '../../../../utils/priceTools'
import { userCategories } from '../../../../utils/userClaims'
import { DispensaryTestKitRequest } from '../../../dispensaries/test-kit-request/DispensaryTestKitRequest'
import TextField from '../../../form/TextField'
import CircularProgressVariant from '../../../global/CircularProgressVariant'
import Link from '../../../global/Link'
import { FulfillmentTimeAcknowledgement } from '../FulfillmentTimeAcknowledgement'
import ItemVariantOptions from '../ItemVariantOptions'
import PaymentTimingOptions from '../PaymentTimingOptions'
import { TestKitLegalTerms } from '../TestKitLegalTerms'
import { TestKitPaymentAgreement } from '../TestKitPaymentAgreement'
import { TruemedModal } from '../TruemedModal'
import * as Styled from './styled'

export interface ItemControlsProps {
  className?: string
  addToCart: AddToCart | null
  availableVariantOptions: Record<AttributeName, AttributeValue[]> | null
  error: ReactElement | null
  onVariantSelected: (itemValue: string) => void
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  submitting: boolean
  variantData: FormattedVariantData
  variantLabelStatus: ProductLabelStatus | undefined
  quantityInCart: number | undefined
  itemUrlKey: string | null | undefined
  labelInformationInCart: { label: string; value: string }[] | undefined
  item: ConfigurableProductDetailsFragment
  sanityParentProduct?: SanityParentProductFragment | null
  containerType: 'modal' | 'page'
}

export type AddToCart = (options: AddToCartOptions) => void

export interface AddToCartOptions {
  qty: string
  orderType: string | number
  autoShipInterval: string
  // Pay upfront option data
  payUpfrontOptionId?: string
  payUpfrontOptionValue?: string
  // Label Design Information for PL
  desiredName?: string
  additionalDetails?: string
}

interface FieldValues {
  // nest dynamic fields for variant attributes to avoid index signature on FieldValues
  variant: Record<AttributeName, AttributeValue>

  qty: { label: string; value: string }
  dfh_autoship_interval: string
  dfh_order_type: number

  // pay upfront option
  pay_upfront_option_id?: string
  pay_upfront_option_value?: string

  // label information
  desired_name: string
  additional_notes?: string
}

// ----- New internal components -----
const LabelInformationSection: FC<{
  labelInformation: { labelName: string; labelDetails: string } | undefined
  submitting: boolean
}> = ({ labelInformation, submitting }) => {
  return (
    <>
      <p className="small-paragraph small-margin-bottom label-information-header">
        Label information is required before adding this item to your cart
      </p>
      {!!labelInformation && (
        <Alert type="attention">
          {`To update your label information at this time, you will need to remove the item
                from your cart and re-enter the information on this page.`}
        </Alert>
      )}
      <div className="label-information">
        {labelInformation ? (
          <>
            <MuiTextField
              name="desired_name_readonly"
              label="Desired Name"
              value={labelInformation.labelName}
              helperText={
                <>
                  <Styled.HelperTextEditIcon />
                  Remove from cart to edit
                </>
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{ disableUnderline: true }}
              variant="filled"
              disabled
              fullWidth
            />
            <MuiTextField
              name="additional_notes_readonly"
              label="Additional Details (fallback Desired Name, notes)"
              value={labelInformation.labelDetails}
              helperText={
                <>
                  <Styled.HelperTextEditIcon />
                  Remove from cart to edit
                </>
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{ disableUnderline: true }}
              variant="filled"
              fullWidth
              disabled
              multiline
            />
          </>
        ) : (
          <>
            <TextField
              name="desired_name"
              label="Desired Name"
              disabled={submitting}
              rules={{ required: true }}
            />
            <TextField
              name="additional_notes"
              label="Additional Details (fallback Desired Name, notes)"
              disabled={submitting}
              multiline
            />
          </>
        )}
      </div>
    </>
  )
}

const AutoShipSection: FC<{
  control: Control<FieldValues>
  isDisabledFormInput: boolean
  currentOrderType: number
  containerType: 'modal' | 'page'
}> = ({ control, containerType, isDisabledFormInput, currentOrderType }) => {
  return (
    <Controller
      control={control}
      name="dfh_order_type"
      disabled={isDisabledFormInput}
      defaultValue={1}
      render={(field) => {
        const getField = (value: number) => ({
          value,
          name: field.name,
          disabled: isDisabledFormInput,
          checked: field.value === value,
          onCheckedChange: (checked: boolean) => checked && field.onChange(value),
        })
        return (
          <div className="flex justify-between items-start gap-2 divide-x divide-brand-rule-grey !p-0">
            <fieldset className="flex justify-between items-start gap-2 divide-x divide-brand-rule-grey !p-0 border-0 m-0">
              <legend className="sr-only">Order Type</legend>
              <Label
                className={cn(
                  'flex-1 px-2.5 md:px-5 py-5 xl:py-6 flex items-center gap-3 xl:gap-3.5 text-sm',
                  containerType === 'page' && 'xl:px-10 xl:text-base',
                )}
              >
                <Checkbox {...getField(1)} />
                One time order
              </Label>
              <div
                className={cn(
                  'flex-1 px-2.5 md:px-5 py-5 xl:py-6 flex flex-col justify-center gap-3 xl:gap-3.5 ',
                  containerType === 'page' && 'xl:px-10',
                )}
              >
                <Label
                  className={cn(
                    'flex items-center gap-3 xl:gap-3.5 text-sm',
                    containerType === 'page' && 'xl:text-base',
                  )}
                >
                  <Checkbox {...getField(2)} />
                  Subscribe to Save 5%
                </Label>
                {[2, '2'].includes(currentOrderType) && (
                  <Controller
                    id="dfh_autoship_interval"
                    name="dfh_autoship_interval"
                    defaultValue={autoShipIntervalOptions[0].value}
                    control={control}
                    disabled={isDisabledFormInput}
                    render={(controllerField) => (
                      <div className="flex items-center gap-3 xl:gap-3.5">
                        <Label className="text-xs" htmlFor="autoship_interval">
                          Ship every
                        </Label>
                        <Select
                          disabled={isDisabledFormInput}
                          onValueChange={controllerField.onChange}
                          defaultValue={controllerField.value}
                        >
                          <SelectTrigger id="autoship_interval" className="w-fit font-medium">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {autoShipIntervalOptions.map((option, index) => (
                                <SelectItem key={index} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  />
                )}
              </div>
            </fieldset>
          </div>
        )
      }}
    />
  )
}

const PrescribeSection: FC<{
  prescription: Prescription
  quantityItemsInEScript: number
  handlePrescribeToPatient: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  dispensaryCartLoading: boolean
  isDisabledFormInput: boolean
  addingToPrescription: boolean
  allowAddToCart: boolean
}> = ({
  prescription,
  quantityItemsInEScript,
  handlePrescribeToPatient,
  dispensaryCartLoading,
  isDisabledFormInput,
  addingToPrescription,
  allowAddToCart,
}) => {
  return (
    <div className="flex justify-between items-center gap-5">
      <p className="text-sm">
        {prescription.products.length > 0 ? (
          <>
            {`${quantityItemsInEScript} item${quantityItemsInEScript > 1 ? 's' : ''} in your eScript. `}
            <Styled.StyledLink to="/escript/build" className="text-xl font-bold">
              Edit here.
            </Styled.StyledLink>
          </>
        ) : (
          'eScript allows you to send your patients a link with all they need.'
        )}
      </p>
      <Button
        size="default"
        variant="secondary"
        key="prescribe-to-patient"
        onClick={handlePrescribeToPatient}
        disabled={
          dispensaryCartLoading || isDisabledFormInput || addingToPrescription || !allowAddToCart
        }
      >
        {addingToPrescription ? <CircularProgressVariant /> : 'Add to eScript'}
      </Button>
    </div>
  )
}

const ItemControlsReactive: FC<ItemControlsProps> = ({
  addToCart,
  availableVariantOptions = {},
  className,
  error,
  onVariantSelected,
  selectedVariant = null,
  submitting,
  variantData,
  variantLabelStatus,
  quantityInCart,
  itemUrlKey,
  labelInformationInCart,
  item,
  containerType,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const { userCategory } = useUserCategory()
  const testKitDelayedBilling = useTestKitDelayedBilling(selectedVariant)
  const payUpfrontOption = usePayUpfrontOption(selectedVariant)
  const showAutoShipAndSaveToUserGroup = useShowAutoShipAndSaveToUserGroup()
  const showAutoShipAndSave = !!featureFlags?.autoShipAndSave && showAutoShipAndSaveToUserGroup
  const [isPayUpfrontNotSelected, setPayUpfrontNotSelected] = useState(false)
  const { loading: dispensaryCartLoading } = useDispensaryCartStatus()
  const itemQuantityConfig = useMemo(
    () =>
      selectedVariant ? resolveItemQuantityConfig(selectedVariant.stock_configuration) : undefined,
    [selectedVariant],
  )

  // collect set of valid quantities from which select options are produced and custom values validated
  const validAddQuantityOptionValues = useMemo(
    () =>
      itemQuantityConfig
        ? validAddQuantityOptionsFor(itemQuantityConfig)({
            curQuantity: quantityInCart ?? 0,
            maxItemOrderQuantityByUserCategory: maxItemOrderQuantityByUserCategory(userCategory),
          })
        : [],
    [itemQuantityConfig, quantityInCart, userCategory],
  )

  const quantityOptions = useMemo(
    () => selectOptionsToDisplay(validAddQuantityOptionValues),
    [validAddQuantityOptionValues],
  )

  // use `null` instead of `undefined` since the latter does not clear the control
  const defaultQuantityOption = useMemo(() => quantityOptions[0] ?? null, [quantityOptions])

  // track previous default quantity for updating quantity selection intelligently
  const [previousDefaultQuantity, setPreviousDefaultQuantity] = useState(
    defaultQuantityOption?.value,
  )

  const labelInformation = useMemo(
    () =>
      labelInformationInCart?.reduce(
        (acc, value, index) => {
          if (labelInformationInCart && labelInformationInCart.length <= 2) {
            acc[`${index === 0 ? 'labelName' : 'labelDetails'}`] = value.value
          }
          return acc
        },
        {} as { labelName: string; labelDetails: string },
      ),
    [labelInformationInCart],
  )

  // Avoid building on each render. Also leave out conditionally rendered fields so the `defaultValue` prop
  // works as expected.
  const [defaultValues] = useState<Pick<FieldValues, 'variant' | 'qty'>>(() => ({
    variant: defaultItemFormValues({
      ...variantData,
      availableVariantOptions,
      item,
    }),
    qty: defaultQuantityOption,
  }))

  const methods = useForm<FieldValues>({
    defaultValues,
  })

  const { control, handleSubmit, register, setError, setValue, trigger, watch } = methods

  const authState = useAuth0()
  const { isAuthenticated } = authState
  const customerId = useCustomerId()
  const dispensary = useDispensaryContext()
  const isProvisionalUser = isAuthenticated && !customerId
  const isPatient = userCategory === userCategories.patient
  const isPractitioner = userCategory === userCategories.practitioner
  const { canPurchaseTestKits } = useUserAccessPolicy()

  const { usedAttributes } = variantData

  const currentAutoshipInterval = watch('dfh_autoship_interval', autoShipIntervalOptions[0].value)
  const currentOrderType = watch('dfh_order_type', 1)
  const currentQty = watch('qty')
  const variantSelections = watch('variant')
  const payUpfrontOptionValue = watch('pay_upfront_option_value')

  useEffect(() => {
    const defaultQuantity = defaultQuantityOption?.value
    if (previousDefaultQuantity !== defaultQuantity) {
      // update current quantity to new default if set to previous default quantity
      if (currentQty && currentQty.value === previousDefaultQuantity) {
        setValue('qty', defaultQuantityOption)
      }
      setPreviousDefaultQuantity(defaultQuantity)
    }
  }, [currentQty, defaultQuantityOption, previousDefaultQuantity, setValue])

  // update current qty selection when no longer valid
  useEffect(() => {
    const currentQtyValue = currentQty ? Number(currentQty.value) : undefined
    const isCurrentQtyValid =
      !!currentQtyValue && validAddQuantityOptionValues.includes(currentQtyValue)
    if (!isCurrentQtyValid) {
      setValue('qty', defaultQuantityOption, {
        shouldDirty: false,
        shouldValidate: false,
      })
    }
  }, [currentQty, defaultQuantityOption, setValue, validAddQuantityOptionValues])

  useEffect(() => {
    if (!payUpfrontOptionValue && !!payUpfrontOption) {
      setPayUpfrontNotSelected(true)
    } else {
      setPayUpfrontNotSelected(false)
    }
  }, [payUpfrontOption, payUpfrontOptionValue])

  const onSubmit = (values: FieldValues) => {
    // enforce testKitPaymentAgree requirement here instead of in element so trigger()
    // in handlePrescribeToPatient() does not trigger requirement error
    //TODO: resolve testKitPaymentAgree not highlighting when required
    if (watch('testKitPaymentAgree') === false && !payUpfrontOptionValue) {
      setError('testKitPaymentAgree', { type: 'required' })
      return
    }
    if (!addToCart) {
      return
    }
    addToCart({
      autoShipInterval: values.dfh_autoship_interval || autoShipIntervalOptions[0].value,
      orderType: values.dfh_order_type ?? 1,
      qty: values.qty.value,
      ...(values.desired_name && {
        desiredName: values.desired_name,
        additionalDetails: values.additional_notes || undefined,
      }),
      payUpfrontOptionId: values.pay_upfront_option_id,
      payUpfrontOptionValue: values.pay_upfront_option_value,
    })
  }

  const hasSelectedVariantCombination =
    Object.values(variantSelections).filter((value) => value).length === usedAttributes.length

  const combinationNotAvailable = isCombinationNotAvailable({
    hasSelectedVariantCombination,
    variantSelections,
    productIndex: variantData.productIndex,
  })

  const stockMessage = stockMessageDisplay({
    selectedVariant,
    combinationNotAvailable,
  })

  const priceText = currentQty
    ? itemQuantityPrice({
        item: selectedVariant,
        qty: Number(currentQty.value),
        ignoreFalsyAmount: !testKitDelayedBilling,
      })
    : ''

  const { isTestKitSku } = useTestKitProducts()
  const isTestKit = isTestKitSku(selectedVariant?.sku || '')

  const anonymousCart = featureFlags?.anonymousCart ?? false

  const isVariantLabelStatusAbsent = variantLabelStatus === ProductLabelStatus.Absent

  const allowAddToCart =
    (isAuthenticated || !!dispensary || anonymousCart) &&
    (!isTestKit || (isAuthenticated && canPurchaseTestKits)) &&
    !combinationNotAvailable

  const showDispensaryTestKitRequest = isTestKit && (!isAuthenticated || isPatient)

  const showAddToCartActions = !showDispensaryTestKitRequest

  const isDisabledFormInput =
    !allowAddToCart ||
    !addToCart ||
    submitting ||
    !priceText ||
    selectedVariant?.stock_status === 'OUT_OF_STOCK'

  const showAutoShip =
    showAddToCartActions &&
    !isTestKit &&
    !isVariantLabelStatusAbsent &&
    !authState.isLoading &&
    isAuthenticated &&
    !isProvisionalUser

  const showPrescribeToPatient = isPractitioner && featureFlags?.manageEScriptBuilder

  const [addingToPrescription, setAddingToPrescription] = useState(false)
  const [isTruemedOpen, setIsTruemedOpen] = useState(false)

  const { dispatch, prescription } = usePrescriptionBuilder()
  const { showSuccessToast: showPrescribeToPatientToast } = useAddToPrescriptionToast()

  const handlePrescribeToPatient = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    trigger()
      .then((isValid) => {
        if (!isValid) return
        setAddingToPrescription(true)

        // simulate delay for UX
        setTimeout(
          () => {
            if (!selectedVariant?.sku) {
              return
            }
            showPrescribeToPatientToast()
            dispatch(
              prescriptionBuilderActions.add(
                {
                  sku: selectedVariant.sku,
                  quantity: Number(currentQty.value),
                  autoShip: [2, '2'].includes(currentOrderType)
                    ? (Number(currentAutoshipInterval) as 1 | 2 | 3)
                    : null,
                  urlKey: itemUrlKey ?? '',
                },
                'pdp',
              ),
            )
            setAddingToPrescription(false)
          },
          500 + Math.floor(Math.random() * 300),
        )
      })
      .catch(() => {
        // noop
      })
  }

  const quantityItemsInEScript = useMemo(
    () => prescription.products.reduce((acc, { quantity }) => acc + quantity, 0),
    [prescription.products],
  )

  const isOneTimeOrder = Number(currentOrderType) === 1
  const oneTimeOrderMessage = showAutoShipAndSave
    ? 'Save 5% on recurrent orders with Auto-Ship.Set up preferences in the next step. Cancel at any time.'
    : 'Set up Auto-Ship and never run out of the supplements you love! Cancel at any time.'
  const autoShipMessage = isOneTimeOrder
    ? oneTimeOrderMessage
    : 'You will receive a 5% discount on this product.'

  const isTruemedAvailable = useIsTruemedAvailable({
    skus: selectedVariant?.sku ? [selectedVariant?.sku] : [],
  })

  return (
    <>
      <TruemedModal isOpen={isTruemedOpen} onClose={() => setIsTruemedOpen(false)} />

      <FormProvider {...methods}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className={cn(
            'divide-y divide-brand-rule-grey [&>*]:last:border-0 [&>*]:px-2.5 md:[&>*]:px-5 [&>*]:py-5 xl:[&>*]:py-6',
            containerType === 'page' && '[&>*]:xl:px-10',
            className,
          )}
        >
          <div
            className={cn(
              'divide-y divide-brand-rule-grey !p-0 [&>*]:px-2.5 md:[&>*]:px-5 [&>*]:py-5 xl:[&>*]:py-6',
              (item.variants?.length ?? 0) <= 1 && !payUpfrontOption && 'hidden [&+*]:!border-t-0',
              containerType === 'page' && '[&>*]:xl:px-10',
            )}
          >
            {payUpfrontOption && (
              <>
                <input
                  type="hidden"
                  name={'pay_upfront_option_id'}
                  ref={register}
                  value={payUpfrontOption.optionId}
                />
                <PaymentTimingOptions
                  size={containerType === 'modal' ? 'sm' : 'lg'}
                  control={control}
                  showAlert={isPayUpfrontNotSelected}
                  payUpfrontPrice={payUpfrontOption.price}
                  payUpfrontOptionValue={payUpfrontOption.valueString}
                />
              </>
            )}
            {!testKitDelayedBilling && (
              <ItemVariantOptions
                size={containerType === 'modal' ? 'sm' : 'lg'}
                item={item}
                hasSelectedVariantCombination={hasSelectedVariantCombination}
                control={control}
                variantData={variantData}
                onVariantSelected={onVariantSelected}
                variantSelections={variantSelections}
                variantLabelStatus={variantLabelStatus}
              />
            )}
          </div>

          {/* The labels are used in the private site */}
          {(isVariantLabelStatusAbsent || !!labelInformation) && (
            <LabelInformationSection labelInformation={labelInformation} submitting={submitting} />
          )}
          {showAutoShip && (
            <>
              <AutoShipSection
                containerType={containerType}
                control={control}
                isDisabledFormInput={isDisabledFormInput}
                currentOrderType={currentOrderType}
              />
              {containerType === 'modal' && <p className="text-sm">{autoShipMessage}</p>}
            </>
          )}
          {isVariantLabelStatusAbsent && (
            <FulfillmentTimeAcknowledgement
              isDisabled={submitting}
              defaultChecked={!!labelInformation?.labelName}
            />
          )}
          {isTestKit && isAuthenticated && canPurchaseTestKits && (
            <div className="flex-1 space-y-5">
              <TestKitLegalTerms isDisabled={submitting} />
            </div>
          )}
          {/*TODO: determine if this should appear in the same section as TestKitLegalTerms above*/}
          {testKitDelayedBilling && (
            <div className="flex-1 space-y-5">
              <TestKitPaymentAgreement isDisabled={submitting || !!payUpfrontOptionValue} />
            </div>
          )}
          {stockMessage && containerType === 'modal' && (
            <div role="alert" className="text-xl text-brand-orange font-bold">
              {stockMessage}
            </div>
          )}
          {showDispensaryTestKitRequest && selectedVariant?.sku && (
            <DispensaryTestKitRequest productSku={selectedVariant.sku} />
          )}
          {showPrescribeToPatient && (
            <PrescribeSection
              prescription={prescription}
              quantityItemsInEScript={quantityItemsInEScript}
              handlePrescribeToPatient={handlePrescribeToPatient}
              dispensaryCartLoading={dispensaryCartLoading}
              isDisabledFormInput={isDisabledFormInput}
              addingToPrescription={addingToPrescription}
              allowAddToCart={allowAddToCart}
            />
          )}
          {showAddToCartActions && (
            <>
              <div
                data-cy="add-to-cart-button-container"
                className={
                  containerType === 'modal'
                    ? 'sticky bottom-0 w-full bg-white z-10 !p-[18px] rounded-b-[10px]'
                    : 'flex flex-col xl:flex-row justify-between items-center gap-5 xl:gap-10'
                }
              >
                {containerType === 'page' &&
                  (stockMessage ? (
                    <p
                      role="alert"
                      className="text-xl xl:text-xl text-center md:text-left text-brand-orange font-bold"
                    >
                      {stockMessage}
                    </p>
                  ) : (
                    showAutoShip && (
                      <p className="text-xs xl:text-sm text-center md:text-left">
                        {autoShipMessage}
                      </p>
                    )
                  ))}
                <div className="flex items-center justify-center gap-10 flex-shrink-0">
                  {isPatient && isTestKit ? (
                    <DispensaryTestKitRequest productSku={selectedVariant?.sku || ''} />
                  ) : (
                    <>
                      <Styled.DropdownCreatable
                        id="qty"
                        name="qty"
                        className="text-xl font-bold"
                        menuPlacement="top"
                        control={control}
                        aria-label="Select quantity"
                        options={quantityOptions}
                        disabled={dispensaryCartLoading || isDisabledFormInput}
                        formatCreateLabel={(inputValue: string) => inputValue}
                        isValidNewOption={(inputValue, _selectValue, selectOptions) => {
                          if (selectOptions.some(({ value }) => value === inputValue)) {
                            return false
                          }
                          const numberValue = Number(inputValue)
                          return (
                            !Number.isNaN(numberValue) &&
                            validAddQuantityOptionValues.includes(numberValue)
                          )
                        }}
                      />
                      <Button
                        key="add-to-cart"
                        size="sm"
                        type="submit"
                        className="text-xl font-bold"
                        disabled={dispensaryCartLoading || isDisabledFormInput || !allowAddToCart}
                      >
                        {submitting ? <CircularProgressVariant /> : 'Add to Cart'}
                      </Button>
                    </>
                  )}
                </div>
                {containerType === 'modal' && (
                  <div className="pt-5 text-center sm:hidden">
                    <Button className="m-auto" variant="text" asChild>
                      <Link to={`/products/${itemUrlKey}`}>
                        See full product details <ChevronRight />
                      </Link>
                    </Button>
                  </div>
                )}
              </div>

              {isTruemedAvailable && containerType === 'page' && (
                <p>
                  <strong className="font-bold rounded-[60px] bg-black/5 py-2 px-4 inline-block mr-3">
                    Truemed
                  </strong>
                  This product is eligible for payment via HSA/FSA.{' '}
                  <Button
                    variant="text"
                    className="font-bold"
                    onClick={() => setIsTruemedOpen(true)}
                    type="button"
                  >
                    Learn more
                  </Button>
                </p>
              )}
            </>
          )}
          {error && <Styled.ErrorMessage variant="body2">{error}</Styled.ErrorMessage>}
          <Styled.ShakableLoginToSeePrice isProductPage toggle={false} />
        </form>
      </FormProvider>
    </>
  )
}

const ItemControls: React.FC<ItemControlsProps> = (props) => (
  <Subscribe>
    <ItemControlsReactive {...props} />
  </Subscribe>
)

export default ItemControls
